import { gql } from "graphql-request";
import { useTranslations } from "next-intl";

import { TextBlock } from "@shared/components";
import { Text } from "@shared/elements";

import { TContactFields } from "__generated__/types";

import { MESSAGE_PREFIX } from "src/constants";

type Props = Pick<TContactFields, "contact" | "contactDetails">;

type Row = { label: string; value: string; href: string };

export default function Contact({ contact, contactDetails }: Props) {
  const t = useTranslations(`${MESSAGE_PREFIX}components.Contact`);

  const rows: Row[] = [
    contactDetails?.email && {
      label: t("emailLabel"),
      value: contactDetails.email,
      href: `mailto:${contactDetails.email}`,
    },
    contactDetails?.phone && {
      label: t("phoneLabel"),
      value: contactDetails.phone,
      href: `tel:${contactDetails.phone}`,
    },
    contactDetails?.instagram && {
      label: t("instagramLabel"),
      value: `@${contactDetails.instagram}`,
      href: `https://instagram.com/${contactDetails.instagram}`,
    },
  ].filter(Boolean) as Row[];

  const hasStructured = rows.length > 0;
  if (!hasStructured && !contact) return null;

  return (
    <div className="md:w-[700px] max-w-screen-md px-4 md:px-0 mx-auto">
      <Text as="h1" size="3xl" weight="bold" className="mb-4">
        {t("title")}
      </Text>
      <Text as="p" className="mb-4">
        {t("contactUs")}
      </Text>
      {hasStructured ? (
        <dl className="w-full mt-8 divide-y divide-gray-200 border-t border-gray-200">
          {rows.map(({ label, value, href }) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 py-5" key={label}>
              <Text weight="semibold">{label}</Text>
              <Text>
                <a
                  href={href}
                  className="underline text-primary-600 break-all"
                  {...(href.startsWith("https://") && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                >
                  {value}
                </a>
              </Text>
            </div>
          ))}
        </dl>
      ) : contact ? (
        <TextBlock content={contact} />
      ) : null}
    </div>
  );
}

Contact.messages = [`${MESSAGE_PREFIX}components.Contact`];

Contact.graphql = {
  fragments: gql`
    fragment contactFields on Call {
      contact
      contactDetails {
        email
        phone
        instagram
      }
    }
  `,
};
